import { Alert, Box, List, Text, Title } from "@mantine/core";
import { generatePath, Link, useParams } from "react-router";
import { AnchorLink } from "../../../../components/AnchorLink/AnchorLink";
import { TruncatedList } from "../../../../components/TruncatedList/TruncatedList";
import { AlertPresets } from "../../../../mantine/mantinePresets";
import { ROUTES } from "../../../../routes";
import urls from "../../../../urls";
import { PARAGRAPH_6_TABS } from "../../Paragraph6.constants";
import type {
  Paragraph6ContractResponse,
  Paragraph6CreatableCredit,
  Paragraph6CreditError
} from "../../Paragraph6.types";
import { EegAnlageIncompleteErrors } from "./ErrorMessages/EegAnlageIncomplete";
import { EnergyErrors } from "./ErrorMessages/EnergyErrors";
import { ExactlyOneOperatorErrors } from "./ErrorMessages/ExactlyOneOperatorErrors";
import { FiktiveStrommengenHints } from "./ErrorMessages/FiktiveStrommengenHints";
import { GeneratorsWithIncompleteVeraeusserungsformenErrors } from "./ErrorMessages/GeneratorsWithIncompleteVeraeusserungsformenErrors";
import { MasterErrors } from "./ErrorMessages/MasterErrors";
import { NetztransparenzValuesError } from "./ErrorMessages/NetztransparenzValuesError";
import "./Paragraph6CreditErrorMessage.scss";

export interface Paragraph6CreditErrorMessageProps {
  paragraph6CreditErrors: Array<Paragraph6CreditError>;
  creatableCredits?: Array<Paragraph6CreatableCredit>;
  contracts?: Array<Paragraph6ContractResponse>;
}

export interface Paragraph6CreditErrorMessageDetailProps {
  paragraph6CreditErrors: Array<Paragraph6CreditError>;
  contract?: Paragraph6ContractResponse;
}

function Paragraph6CreditErrorMessage({
  paragraph6CreditErrors,
  creatableCredits = [],
  contracts
}: Paragraph6CreditErrorMessageProps) {
  const paragraph6CreditWithActionsErrors = paragraph6CreditErrors.filter(
    (error) =>
      Object.values(error).some((value) => value === true) ||
      error.generators_with_incomplete_veraeusserungsformen.length > 0 ||
      error.eeg_anlage_incomplete.length > 0
  );

  const paragraph6CreditWithNoActionsErrors = paragraph6CreditErrors.filter(
    (error) =>
      Object.values(error.netztransparenz_values_missing).some(
        (value) => value.length > 0
      )
  );

  const creatableBillingPeriods = [
    ...paragraph6CreditErrors.map((error) => error.billing_period),
    ...creatableCredits
  ].filter((billingPeriod) => billingPeriod.fiktive_strommengen_are_imprecise);

  const contractsWithActionErrors = groupByContract(
    paragraph6CreditWithActionsErrors,
    (error) => error.billing_period.contract,
    contracts
  );

  const contractsWithNoActionsErrors = groupByContract(
    paragraph6CreditWithNoActionsErrors,
    (error) => error.billing_period.contract,
    contracts
  );

  const contractsWithHints = groupByContract(
    creatableBillingPeriods,
    (billingPeriod) => billingPeriod.contract,
    contracts
  );

  const hasBlockingErrors =
    contractsWithActionErrors.length > 0 ||
    contractsWithNoActionsErrors.length > 0;

  return (
    <Box className="Paragraph6CreditErrorMessage" mt="md">
      <Alert
        color={AlertPresets.warning.color}
        icon={AlertPresets.warning.icon}
      >
        {hasBlockingErrors ? (
          <>
            <Title fw={600} order={5}>
              Mindestens eine Gutschrift kann nicht erstellt werden.
            </Title>
            <Text>
              Einige Ihrer Verträge benötigen noch Informationen, um
              Gutschriften zu erstellen.
            </Text>
          </>
        ) : (
          <Title fw={600} order={5}>
            Bitte beachten Sie die folgenden Hinweise.
          </Title>
        )}

        {contractsWithActionErrors.length > 0 && (
          <Text fw="bold" mt="md">
            Bitte ergänzen Sie die folgenden Angaben:
          </Text>
        )}
        <TruncatedList
          items={contractsWithActionErrors.map(({ contract, items }) => (
            <ErrorContent
              contract={contract}
              errors={items}
              key={contract.id}
            />
          ))}
        />

        {contractsWithNoActionsErrors.length > 0 && (
          <Text fw="bold" mt="md">
            Bitte warten Sie, bis die folgenden Daten vorliegen:
          </Text>
        )}
        <TruncatedList
          items={contractsWithNoActionsErrors.map(({ contract, items }) => (
            <ErrorContent
              contract={contract}
              errors={items}
              key={contract.id}
              mode="no-actions"
            />
          ))}
        />

        {contractsWithHints.length > 0 && (
          <Text fw="bold" mt="md">
            Diese Gutschriften lassen sich erstellen, bitte prüfen Sie jedoch:
          </Text>
        )}
        <TruncatedList
          items={contractsWithHints.map(({ contract, items }) => (
            <HintContent
              billingPeriods={items}
              contract={contract}
              key={contract.id}
            />
          ))}
        />

        <Text my="sm">
          Bei Fragen stehen Ihnen unsere{" "}
          <AnchorLink
            href={urls.optiSupport("gutschrift-erstellen-paragraph-6")}
          >
            opti.node-Hilfe
          </AnchorLink>{" "}
          und unser{" "}
          <AnchorLink href={urls.optiSupport()}>Support-Team</AnchorLink> gerne
          zur Verfügung.
        </Text>
      </Alert>
    </Box>
  );
}

function groupByContract<T>(
  items: Array<T>,
  contractIdOf: (item: T) => string,
  contracts?: Array<Paragraph6ContractResponse>
) {
  return (
    contracts
      ?.map((contract) => ({
        contract,
        items: items.filter((item) => contractIdOf(item) === contract.id)
      }))
      .filter(({ items }) => items.length > 0) || []
  );
}

function ErrorContent({
  contract,
  errors,
  mode = "with-actions"
}: {
  contract: Paragraph6ContractResponse;
  errors: Array<Paragraph6CreditError>;
  mode?: "with-actions" | "no-actions";
}) {
  const { projectId } = useParams<{ projectId: string }>();
  const contractPath = projectId
    ? generatePath(
        `${ROUTES.paragraph6.entry}/${PARAGRAPH_6_TABS.CONTRACTS}/:contractId`,
        {
          projectId,
          contractId: contract.id
        }
      )
    : undefined;

  return (
    <>
      {contractPath ? (
        <Link to={contractPath}>{contract.name}:</Link>
      ) : (
        <strong>{contract.name}:</strong>
      )}{" "}
      {mode === "no-actions" && (
        <span>
          (Gutschrift{errors.length > 1 ? "en" : ""}{" "}
          {errors
            .map(
              (error) =>
                `${error.billing_period.start_date} - ${error.billing_period.end_date}`
            )
            .join(", ")}
          )
        </span>
      )}
      <List withPadding>
        {mode === "with-actions" && (
          <>
            <MasterErrors paragraph6CreditErrors={errors} />
            <ExactlyOneOperatorErrors paragraph6CreditErrors={errors} />
            <EnergyErrors paragraph6CreditErrors={errors} />
            <GeneratorsWithIncompleteVeraeusserungsformenErrors
              paragraph6CreditErrors={errors}
            />
            <EegAnlageIncompleteErrors paragraph6CreditErrors={errors} />
          </>
        )}
        {mode === "no-actions" && (
          <>
            <NetztransparenzValuesError paragraph6CreditErrors={errors} />
          </>
        )}
      </List>
    </>
  );
}

function HintContent({
  contract,
  billingPeriods
}: {
  contract: Paragraph6ContractResponse;
  billingPeriods: Array<Paragraph6CreatableCredit>;
}) {
  const { projectId } = useParams<{ projectId: string }>();
  const contractPath = projectId
    ? generatePath(
        `${ROUTES.paragraph6.entry}/${PARAGRAPH_6_TABS.CONTRACTS}/:contractId`,
        {
          projectId,
          contractId: contract.id
        }
      )
    : undefined;

  return (
    <>
      {contractPath ? (
        <Link to={contractPath}>{contract.name}:</Link>
      ) : (
        <strong>{contract.name}:</strong>
      )}
      <List withPadding>
        <FiktiveStrommengenHints billingPeriods={billingPeriods} />
      </List>
    </>
  );
}

export { Paragraph6CreditErrorMessage };
